import styled from 'styled-components';
import { pxToRem } from 'utils/convert';
import { SkeletonContainer, Skeleton } from 'ui/skeleton';
import DateRangePickerRelative from 'core/dateRangePickerRelative';
import { GhostButton, GhostInvertedButton } from 'ui/button';
import { spacing } from 'ui/theme';
import RGL, { WidthProvider } from 'react-grid-layout';

export const StyledCoreDateRangePickerRelative = styled(DateRangePickerRelative)`
  &&& {
    width: 100%;
    [data-role='accordion'] {
      width: 100%;
    }
  }
`;

export const CampaignTableAnchor = styled.a`
  font-weight: 600; /** css file replacement */
  color: #333; /** css file replacement */
  &:hover {
    text-decoration: underline;
  }
`;

export const DashboardWidgetActions = styled.div`
  position: absolute;
  right: ${spacing.half};
  top: ${spacing.quarter};
  opacity: 0;
  transition: opacity 0.2s;
  display: flex;
  gap: ${spacing.half};
`;

export const DashboardWidgetName = styled.div<{ theme: 'dark' | 'default' }>`
  position: absolute;
  top: ${spacing.threeQuarter};
  width: 100%;
  color: ${({ theme }) => (theme === 'default' ? '#616161' : '#D6D6D6')};
  text-align: center;
  pointer-events: none;
  font-size: ${pxToRem(14)};
  font-weight: 600;
`;

export const ActionDefaultGhostButton = styled(GhostButton)`
  padding: 0;
`;

export const ActionDarkGhostButton = styled(GhostInvertedButton)`
  padding: 0;
`;

export const StyledSkeleton = styled(Skeleton)`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
`;

export const DashboardCSSWrapper = styled.div`
  &&&& {
    &[data-theme='dark'] {
      .cssDashboardGrid {
        background-color: #222; /** css file replacement */
      }
      .react-resizable-handle {
        &:after {
          border-right: ${pxToRem(2)} solid rgba(255, 255, 255, 0.4);
          border-bottom: ${pxToRem(2)} solid rgba(255, 255, 255, 0.4);
        }
      }
      ${CampaignTableAnchor} {
        color: #2e8ece; /** css file replacement */
        &:hover {
          color: #9fcbe8; /** css file replacement */
        }
      }
    }
    .highcharts-graph [data-number-dark='false'][data-invert='true'][data-custom-theme='true'] {
      stroke: #444;
    }
    .highcharts-container [data-number-dark='false'][data-invert='true'][data-custom-theme='true'] {
      margin: auto;
    }
    .sparklineContainer {
      width: calc(100% - ${pxToRem(20)} - ${pxToRem(20)});
      max-width: ${pxToRem(300)};
      margin: auto;
    }
    .cssToolbarDatePicker {
      /** css file replacement */
      text-align: right;
      margin-right: ${pxToRem(10)};
      white-space: nowrap;
      > button[data-role='dropdown-button'] {
        min-height: ${pxToRem(39)};
      }
    }
    .cssDashboardPanel {
      /** css file replacement */
      border-radius: ${pxToRem(6)};
      transition: margin 0.2s, box-shadow 0.2s;
      z-index: 1;
      &:hover {
        ${DashboardWidgetActions} {
          opacity: 1;
        }
        &:not(.static, .noResize) {
          .react-resizable-handle,
          .dragHandle {
            opacity: 1;
          }
        }
      }
      &.static {
        cursor: default;
      }
      &.noDragHandle {
        cursor: grab;
      }
      .resizing {
        z-index: 1;
      }
      .react-draggable-dragging {
        margin: -${pxToRem(4)} 0 0 0;
        box-shadow: 0 ${pxToRem(2)} ${pxToRem(4)} ${pxToRem(2)} rgba(0, 0, 0, 0.25) !important;
        z-index: 3;
      }
      + .react-grid-item.react-grid-placeholder {
        background: #2e8ece;
        opacity: 0.2;
        transition-duration: 100ms;
        z-index: 0;
        border-radius: ${pxToRem(6)};
        user-select: none;
        pointer-events: none;
      }
      .cssDashboardDragHandle {
        padding: ${pxToRem(10)};
        height: ${pxToRem(20)};
        width: 100%;
        cursor: grab;
        fill: #999; /** css file replacement */
        opacity: 0;
        transition: fill 0.2s, opacity 0.2s;
      }
      .noDrag .cssDashboardDragHandle {
        background: #444; /** css file replacement */
        cursor: not-allowed;
      }
      .react-resizable-handle {
        position: absolute;
        width: ${pxToRem(20)};
        height: ${pxToRem(20)};
        bottom: ${pxToRem(2)};
        right: ${pxToRem(2)};
        cursor: se-resize;
        opacity: 0;
        &:after {
          content: '';
          position: absolute;
          right: ${pxToRem(3)};
          bottom: ${pxToRem(3)};
          width: ${pxToRem(5)};
          height: ${pxToRem(5)};
          border-right: ${pxToRem(2)} solid rgba(0, 0, 0, 0.4);
          border-bottom: ${pxToRem(2)} solid rgba(0, 0, 0, 0.4);
        }
      }
      [data-narrow='true'] > [data-type='number'] {
        > * {
          padding: 0;
        }
      }
      [data-role='pagination'] {
        > * {
          border: none !important;
          &[data-selected='true'] {
            background-color: #54a2d7;
          }
        }
      }
    }
  }
`;

export const StyledGridLayout = styled(WidthProvider(RGL))`
  background: #d6d6d6; /** css file replacement */
  min-height: calc(
    100vh - ${pxToRem(178)} - ${pxToRem(78)}
  ); /** css file replacement, toolbarHeight and headerHeight */
  min-width: 100%;
  .react-draggable-transparent-selection & * {
    /** css file replacement */
    user-select: none;
  }
`;

export const ToolbarContainer = styled.div`
  display: flex;
  justify-content: flex-end;
`;

export const ToolbarMockLabel = styled.div`
  display: inline-flex;
  color: #999; /** css file replacement */
  padding: ${pxToRem(10)} ${pxToRem(20)}; /** css file replacement */
  margin-right: ${pxToRem(5)}; /** css file replacement */
  text-align: center;
  text-transform: uppercase;
`;

export const DateRangePickerWrapper = styled.div`
  margin-right: ${pxToRem(10)};
`;

export const StyledSkeletonContainer = styled(SkeletonContainer)`
  height: ${({ isLoading }) => (isLoading ? pxToRem(1000) : '100%')};
  position: relative;
`;

export const DragHandleContainer = styled.div`
  position: absolute;
  top: 0;
  z-index: 1;
  > * {
    opacity: 1;
  }
`;

export const DashboardMetricWidgetCssWrapper = styled.div`
  &&&&& {
    height: 100%;
    &[data-invert='true'] + [data-role='name'] {
      color: #fff;
    }
    .cssMetricWidgetChart {
      padding: ${pxToRem(40)} ${pxToRem(10)} ${pxToRem(10)};
      box-sizing: border-box;
      align-self: center;

      [data-narrow='true'] > &[data-type='number'] {
        > * {
          padding: 0;
        }
      }
      &[data-type='number'] {
        padding: 0;
        height: 100%;
        justify-content: center;
        > * {
          padding: ${pxToRem(40)} 0 0 0;
        }
      }
      .highcharts-container {
        overflow: visible !important;
        z-index: 2;
      }
    }
  }
`;
